// ============================================================================
// RATE PLAN SERVICE
// ============================================================================
// Business logic and API calls for Rate Plan module

import { get, post, put, del } from '../client/axiosClient';
import { ratePlanEndpoints } from '../endpoints/ratePlan.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  RatePlan,
  CreateRatePlanRequest,
  UpdateRatePlanRequest,
  RatePlanSearchRequest,
} from '../types';

/**
 * Rate Plan Service Class
 */
class RatePlanService {
  /**
   * Get all rate plans (paginated)
   */
  async getAll(params?: RatePlanSearchRequest): Promise<PaginatedResponse<RatePlan>> {
    const endpoint = params
      ? buildEndpointWithQuery('/RatePlan', params)
      : ratePlanEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  /**
   * Get rate plan by ID
   */
  async getById(id: ID): Promise<ApiResponse<RatePlan>> {
    const response = await get<ApiResponse<RatePlan>>(ratePlanEndpoints.getById(id));
    return response.data;
  }

  /**
   * Create new rate plan
   */
  async create(data: CreateRatePlanRequest): Promise<ApiResponse<RatePlan>> {
    const response = await post<ApiResponse<RatePlan>>(
      ratePlanEndpoints.create(),
      data
    );
    return response.data;
  }

  /**
   * Update rate plan
   */
  async update(id: ID, data: UpdateRatePlanRequest): Promise<ApiResponse<RatePlan>> {
    const response = await put<ApiResponse<RatePlan>>(
      ratePlanEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * Delete rate plan
   */
  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(ratePlanEndpoints.delete(id), {
      params: { rowVersion },
    });
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const ratePlanService = new RatePlanService();
export default ratePlanService;
